import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { Checkout } from "../Checkout";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";
import { FontStyle } from "@swiggy-private/dls-web-theme/dist/components/Fonts";
import { GlobalStyles } from "@swiggy-private/dls-web-theme/dist/components/GlobalStyles";
import { BaseCssTheme } from "@swiggy-private/dls-web-common/dist/components/themes/BaseCss/BaseCssTheme";
import { GetSwiggyHeadersResponsePayload } from "@swiggy-private/native-bridge/dist/interfaces";
import { resolveTheme, getDesignLanguage } from "@swiggy-private/aatm-nirbhar-ui/dist/lib/utils/theme";
import { loadTheme } from "@swiggy-private/dls-web-theme/dist/components/LoadTheme";
import { Container } from "./styled";
import { BaseTheme } from "@swiggy-private/dls-web-common/dist/components/themes/precomputed/Base/BaseTheme";
import { CartShimmer, CommonUIWrapper, QuickCheckoutCartShimmer } from "@swiggy-private/core-ui";
import { shouldShowQuickCheckoutShimmer } from "@client/helpers/quickCheckout";
import { GlobalDisableCopyPaste } from "../styles/Global";

type Dict<T> = { [header: string]: T | undefined };

// had to duplicate for unblocking static build generation
declare global {
    interface Window {
        cssThemeLoaded?: boolean;
        selectedTheme?: "base" | "toing" | "dineout";
    }
}

function App(props: {
    apiHost: string;
    paymentHost: string;
    analyticsHost: string;
    headers: Dict<string>;
    path: string;
    simplPostLinkingRedirectUrl: string;
}): React.ReactElement {
    const linkRef = useRef<HTMLLinkElement | null>(null);
    const [currentTheme, setCurrentTheme] = React.useState<string>("base");
    const [isCssLoaded, setIsCssLoaded] = React.useState(false);

    const handleCssLoad = () => {
        window.cssThemeLoaded = true;
        setIsCssLoaded(true);
    };

    const onBack = useCallback(() => {
        return;
    }, []);

    useEffect(() => {
        const theme = window.selectedTheme || resolveTheme(props.headers as GetSwiggyHeadersResponsePayload);
        setCurrentTheme(theme);
        if (window.cssThemeLoaded === true) {
            handleCssLoad();
            return;
        }
        linkRef.current = loadTheme(theme);

        if (linkRef.current) {
            linkRef.current.addEventListener("load", handleCssLoad);
        }

        return () => {
            if (linkRef.current) {
                linkRef.current.removeEventListener("load", handleCssLoad);
                linkRef.current.remove();
                linkRef.current = null;
            }
        };
    }, [props.headers]);

    const designLanguage = useMemo(() => getDesignLanguage(currentTheme), [currentTheme]);

    const showQuickCheckoutShimmer = useMemo(
        () => shouldShowQuickCheckoutShimmer(props.headers),
        [props.headers],
    );

    const shimmer = useMemo(
        () => (showQuickCheckoutShimmer ? <QuickCheckoutCartShimmer /> : <CartShimmer />),
        [showQuickCheckoutShimmer],
    );

    return (
        <ThemeProvider
            theme={isCssLoaded ? BaseCssTheme : BaseTheme}
            colorMode="light"
            design_language={designLanguage}
            key={`${designLanguage}-${isCssLoaded}`}
        >
            <GlobalDisableCopyPaste />
            <GlobalStyles />
            <FontStyle />
            <Container>
                {isCssLoaded && designLanguage ? (
                    <Checkout {...props} />
                ) : (
                    <CommonUIWrapper
                        isQuickCheckout={showQuickCheckoutShimmer}
                        content={shimmer}
                        header={<></>}
                        onBack={onBack}
                        grayBackground
                    />
                )}
            </Container>
        </ThemeProvider>
    );
}

export default App;
